/**
 * Utils for 'the JavaScript language'.
 */

/**
 * Create a function that delays invoking until after enough time have elapsed
 * since the last time the function was invoked.
 *
 * Use lodash for a more powerful version with options.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {Function} The debounced function.
 * @example
 *
 * // Only called when resizing hasn't happened for 250 miliseconds
 * this.on(window, 'resize', debounce(this.handleWindowResize, 250));
 */
export function debounce(func, wait) {
  let timeout;
  return function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(function debouncedTimeout() {
      timeout = null;
      func(...args);
    }, wait);
  };
}

/**
 * Create a function that only invokes at most once within the specified window
 * of time.
 *
 * Called once right away, then at most once every X miliseconds and finally
 * once after the invokations have stopped (i.e. the 'trailing' call).
 *
 * Subsequent calls to the throttled function return the result of the last
 * invocation that passed.
 *
 * Use lodash for a more powerful version with options.
 *
 * @param {Function} func - The function to throttle.
 * @param {number} wait - The number of milliseconds to throttle invocations to.
 * @returns {Function} The throttled function.
 * @example
 *
 * // Called at most four times per second while resizing
 * this.on(window, 'resize', throttle(this.handleWindowResize, 250));
 */
export function throttle(func, wait) {
  let isThrottled = false;
  let throttleArgs = null;
  let result;

  function throttled(...args) {
    if (isThrottled) {
      throttleArgs = args;
      return result;
    }

    isThrottled = true;
    result = func(...args);

    setTimeout(function throttleTimeout() {
      isThrottled = false;
      if (throttleArgs) {
        throttled(...throttleArgs);
        throttleArgs = null;
      }
    }, wait);

    return result;
  }

  return throttled;
}

/**
 * Get a random integer between two values (inclusive).
 *
 * @param {number} min - Minimum value.
 * @param {number} max - Maximum value.
 * @returns {number}
 * @example
 *
 * getRandomIntBetween(5, 10);
 * // => 7
 */
export function getRandomIntBetween(min, max) {
  /* eslint-disable no-param-reassign */
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Cycle through values from left to right.
 *
 * @param {*} current - The current value in the cycle.
 * @param  {...*} values - Values to cycle through.
 * @returns {*} The next value, or first if at the end of the cycle.
 * @example
 *
 * const current = 1;
 * cycleValues(current, 1, 2, 3);
 * // => 2
 *
 * const current = 'c';
 * cycleValues(current, 'a', 'b', 'c');
 * // => 'a'
 */
export function cycleValues(current, ...values) {
  const index = values.indexOf(current);
  if (index === -1) {
    throw new Error(`'${current}' doesn't exist in supplied values.`);
  }
  return index + 1 >= values.length ? values[0] : values[index + 1];
}

/**
 * Conditionally assign properties to a new object.
 *
 * @param {Function} condition - Condition that must return true for the
 *   property to be included in the resulting object. Called for each property
 *   with (value, key, object).
 * @param {...object} objects - Objects to assign.
 * @returns {object} The new object.
 */
export function conditionalAssign(condition, ...objects) {
  const filtered = objects.map((obj) =>
    Object.keys(obj).reduce((result, key) => {
      if (!condition(obj[key], key, obj)) {
        return result;
      }
      return Object.assign(result, { [key]: obj[key] });
    }, {}),
  );
  return Object.assign({}, ...filtered);
}

/**
 * Run a function after the next browser paint.
 *
 * requestAnimationFrame runs in the same frame before the browser paints and
 * any code called there will run right away, unless it's a task scheduling
 * call like another rAF which will then be queued to the next frame.
 *
 * @param {Function} cb - Callback to run.
 */
export function afterNextPaint(cb) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      cb();
    });
  });
}

/* ========================================================================= *\
    Type checks
\* ========================================================================= */

/**
 * Check if a value is an array.
 *
 * A bit pointless given it's just an alias, but it's nice to have the same
 * convention for all type checks.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export const { isArray } = Array;

/**
 * Check if a value is `null`.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isNull(val) {
  return val === null;
}

/**
 * Check if a value is `undefined`.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isUndefined(val) {
  return typeof val === 'undefined';
}

/**
 * Check if a value is `null` or `undefined`.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isNil(val) {
  return val == null;
}

/**
 * Check if a value is a function.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isFunc(val) {
  return typeof val === 'function';
}

/**
 * Check if a value is a number.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isNumber(val) {
  return typeof val === 'number' && !Number.isNaN(val);
}

/**
 * Check if a value is a number or numeric string.
 *
 * @param {*} val - The value to check.
 * @returns {boolean}
 */
export function isNumeric(val) {
  return isNumber(val) || val === String(Number(val));
}

/**
 * Check if a value is a string.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isString(val) {
  return typeof val === 'string';
}

/**
 * Check if a value is a boolean.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isBool(val) {
  return val === true || val === false;
}

/**
 * Check if a value is truthy.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isTruthy(val) {
  return Boolean(val);
}

/**
 * Check if a value is falsy.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isFalsy(val) {
  return !val;
}

/**
 * Check if a value is any non-array object.
 *
 * @param {*} val - Value to check.
 * @returns {boolean}
 */
export function isObject(val) {
  return Boolean(val && typeof val === 'object' && !isArray(val));
}

/**
 * Create a function that does a falsy check against the return value of the
 * passed function when called.
 *
 * @param {Function} func - Function to 'reverse'.
 * @returns {Function}
 * @example
 *
 * // Only keep booleans.
 * someArr.filter(isBool);
 *
 * // Only keep non-booleans.
 * someArr.filter(not(isBool));
 */
export function not(func) {
  return (...args) => !func(...args);
}
